import { useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, ChevronRight, CheckCircle, Loader2, Download, Trash2 } from 'lucide-react';
import { feedbackApi, settingsApi, stripeApi } from '@/services/api';
import { useAuth } from '@/hooks/useAuth';

type Step = 'blocked' | 'survey' | 'thankyou' | 'delete_confirm';

const CHURN_REASONS = [
    { value: 'too_expensive',    label: 'Le prix est trop élevé pour moi' },
    { value: 'missing_feature',  label: 'Il manque une fonctionnalité importante' },
    { value: 'not_useful',       label: "Je n'utilise pas assez l'outil" },
    { value: 'found_alternative', label: "J'ai trouvé une autre solution" },
    { value: 'technical_issues', label: 'Trop de problèmes techniques' },
    { value: 'other',            label: 'Autre raison' },
];

interface Props {
    code?: string; // 'TRIAL_EXPIRED' | 'SUBSCRIPTION_REQUIRED'
}

export function SubscriptionBlockedOverlay({ code }: Props): ReactNode {
    const { user, logout } = useAuth();
    const [step, setStep] = useState<Step>('blocked');
    const [selectedReason, setSelectedReason] = useState('');
    const [comment, setComment] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [exportError, setExportError] = useState('');
    const [deleting, setDeleting] = useState(false);

    const isTrialExpired = code === 'TRIAL_EXPIRED' || !code;
    const title = isTrialExpired
        ? "Votre essai gratuit est terminé"
        : "Votre accès est suspendu";
    const subtitle = isTrialExpired
        ? "Vos 14 jours d'essai se sont écoulés. Toutes vos données sont conservées : souscrivez pour y accéder de nouveau."
        : "Un problème est survenu avec votre abonnement. Régularisez votre situation pour retrouver l'accès.";

    const handleSubmitSurvey = async () => {
        if (!selectedReason) return;
        setSubmitting(true);
        try {
            await feedbackApi.submitChurn({
                email: user?.email || '',
                organizationId: user?.organizationId,
                reason: selectedReason,
                comment: comment.trim() || undefined,
            });
        } catch {
            // Silent fail — survey feedback is best-effort
        } finally {
            setSubmitting(false);
            setStep('thankyou');
        }
    };

    const handleLogout = async () => {
        await logout();
        window.location.href = '/login';
    };

    const handleExport = async () => {
        setExporting(true);
        setExportError('');
        try {
            const blob = await settingsApi.exportData();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `btpal-export-${new Date().toISOString().split('T')[0]}.json`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch {
            // L'échec était avalé : l'artisan cliquait, rien ne se passait, et il n'avait
            // aucun moyen de savoir que la route est réservée à un administrateur.
            setExportError(
                "L'export n'a pas abouti. Il est réservé au propriétaire ou à un administrateur du compte."
            );
        } finally {
            setExporting(false);
        }
    };

    const handleDeleteAccount = async () => {
        setDeleting(true);
        try {
            await settingsApi.deleteAccount();
            await logout();
            window.location.href = '/login';
        } catch {
            setDeleting(false);
        }
    };

    const [checkoutLoading, setCheckoutLoading] = useState<'monthly' | 'annual' | null>(null);
    const [checkoutError, setCheckoutError] = useState('');
    const [waiverAccepted, setWaiverAccepted] = useState(false);

    const handleCheckout = async (plan: 'monthly' | 'annual') => {
        if (!waiverAccepted) {
            setCheckoutError('Vous devez accepter la renonciation au droit de rétractation pour souscrire.');
            return;
        }
        setCheckoutLoading(plan);
        setCheckoutError('');
        try {
            const { url } = await stripeApi.createCheckoutSession(plan, true);
            if (url) {
                window.location.href = url;
            }
        } catch (err: unknown) {
            const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
                || 'Une erreur est survenue. Réessayez ou contactez-nous à contact@btpal.fr.';
            setCheckoutError(message);
            setCheckoutLoading(null);
        }
    };

    return createPortal(
        <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm overflow-y-auto overscroll-contain">
            <div className="min-h-full flex items-center justify-center p-4">
            <div className={`bg-white rounded-lg shadow-2xl w-full overflow-hidden my-4 ${step === 'blocked' ? 'max-w-xl' : 'max-w-md'}`}>

                {/* ── Step 1 : Accès bloqué + pricing ── */}
                {step === 'blocked' && (
                    <div className="p-6 sm:p-8">
                        {/* Header */}
                        <div className="text-center mb-6">
                            <div className="w-14 h-14 rounded-full bg-[#FEF2F2] flex items-center justify-center mx-auto mb-3">
                                <AlertTriangle className="h-7 w-7 text-[#DC2626]" />
                            </div>
                            <h2 className="text-xl font-bold text-[#1E293B] mb-1">{title}</h2>
                            <p className="text-sm text-[#64748B] leading-relaxed">{subtitle}</p>
                        </div>

                        {checkoutError && (
                            <div className="mb-4 p-3 bg-[#FEF2F2] border border-[#FECACA] rounded-lg text-sm text-[#991B1B]">
                                {checkoutError}
                            </div>
                        )}

                        {/* Pricing cards */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
                            {/* Mensuel */}
                            <div className="border border-[#E2E8F0] rounded-lg p-4 flex flex-col">
                                <p className="text-[10px] font-bold text-[#64748B] uppercase tracking-wider mb-2">Mensuel</p>
                                <div className="mb-0.5">
                                    <span className="text-2xl font-bold text-[#1E293B]">59€</span>
                                    <span className="text-xs text-[#64748B]">/mois</span>
                                </div>
                                <p className="text-xs text-[#94A3B8] mb-4">Sans engagement</p>
                                <button
                                    onClick={() => handleCheckout('monthly')}
                                    disabled={checkoutLoading !== null || !waiverAccepted}
                                    className="mt-auto w-full py-2 rounded-lg border border-[#0F2744] text-[#1D4ED8] text-sm font-semibold hover:bg-[#EFF6FF] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                    {checkoutLoading === 'monthly' ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Choisir'}
                                </button>
                            </div>

                            {/* Annuel — recommandé */}
                            <div className="border-2 border-[#0F2744] rounded-lg p-4 relative bg-[#EFF6FF] flex flex-col">
                                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#0F2744] text-white text-[10px] font-bold px-3 py-0.5 rounded-full whitespace-nowrap">
                                    RECOMMANDÉ
                                </div>
                                <p className="text-[10px] font-bold text-[#1D4ED8] uppercase tracking-wider mb-2">Annuel</p>
                                <div className="mb-0.5">
                                    <span className="text-2xl font-bold text-[#1E293B]">49€</span>
                                    <span className="text-xs text-[#64748B]">/mois</span>
                                </div>
                                <p className="text-xs text-[#10B981] font-semibold mb-0.5">soit 588€/an (2 mois offerts)</p>
                                <p className="text-xs text-[#94A3B8] mb-4">Économisez 120€/an</p>
                                <button
                                    onClick={() => handleCheckout('annual')}
                                    disabled={checkoutLoading !== null || !waiverAccepted}
                                    className="mt-auto w-full py-2 rounded-lg bg-[#0F2744] text-white text-sm font-semibold hover:bg-[#1A3A5C] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                    {checkoutLoading === 'annual' ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Choisir'}
                                </button>
                            </div>

                            {/* Entreprise */}
                            <div className="border border-[#E2E8F0] rounded-lg p-4 flex flex-col">
                                <p className="text-[10px] font-bold text-[#64748B] uppercase tracking-wider mb-2">Entreprise</p>
                                <div className="mb-0.5">
                                    <span className="text-xl font-bold text-[#1E293B]">Sur devis</span>
                                </div>
                                <p className="text-xs text-[#94A3B8] mb-4">Serveur isolé · SLA · Support dédié</p>
                                <button
                                    onClick={() => window.open('mailto:contact@btpal.fr?subject=Offre%20Entreprise%20BTPal', '_blank')}
                                    disabled={checkoutLoading !== null}
                                    className="mt-auto w-full py-2 rounded-lg border border-[#E2E8F0] text-[#64748B] text-sm font-semibold hover:border-[#CBD5E1] hover:text-[#1E293B] transition-colors disabled:opacity-50"
                                >
                                    Nous contacter
                                </button>
                            </div>
                        </div>

                        <p className="text-[11px] text-[#64748B] mb-4 px-1">TVA non applicable (article 293 B du CGI).</p>

                        {/* Renonciation au droit de rétractation (Art. L.221-28 13° Code consommation) */}
                        <label className="flex items-start gap-2 mb-4 p-3 bg-[#FFFBEB] border border-[#FDE68A] rounded-lg cursor-pointer">
                            <input
                                type="checkbox"
                                checked={waiverAccepted}
                                onChange={(e) => {
                                    setWaiverAccepted(e.target.checked);
                                    if (e.target.checked) setCheckoutError('');
                                }}
                                className="mt-0.5 h-4 w-4 rounded border-[#FDE68A] text-[#0F2744] focus:ring-[#0F2744] shrink-0"
                            />
                            <span className="text-xs text-[#78350F] dark:text-[#FDE68A] leading-relaxed">
                                J'accepte que le service démarre immédiatement après la souscription et je reconnais
                                renoncer expressément à mon droit de rétractation de 14 jours
                                (art. L.221-28 13° du Code de la consommation).
                                Voir les <a href="/cgu" target="_blank" rel="noopener noreferrer" className="underline font-semibold hover:text-[#0F2744] dark:hover:text-[#FCD34D]">CGU</a>.
                            </span>
                        </label>

                        <p className="text-[11px] text-[#64748B] mb-4 -mt-2 px-1">
                            Résiliable à tout moment depuis votre compte. L'accès reste actif jusqu'à la fin de la période déjà payée.
                        </p>

                        {/* Features incluses */}
                        <div className="bg-[#F8FAFC] rounded-lg px-4 py-3 mb-5 grid grid-cols-2 gap-x-4 gap-y-1.5">
                            {[
                                'Factures & devis illimités',
                                    'Gestion des chantiers',
                                'Prévision de trésorerie',
                                'Export comptable FEC',
                                'Situations de travaux',
                                'Support par email',
                            ].map(f => (
                                <div key={f} className="flex items-center gap-1.5 text-xs text-[#374151]">
                                    <CheckCircle className="h-3.5 w-3.5 text-[#10B981] shrink-0" />
                                    <span>{f}</span>
                                </div>
                            ))}
                        </div>

                        {/* Actions secondaires */}
                        <div className="text-center">
                            <button
                                onClick={() => setStep('survey')}
                                className="w-full text-sm text-[#64748B] hover:text-[#1E293B] py-1.5 transition-colors"
                            >
                                Ce n'est pas pour moi, donner mon avis
                            </button>
                            <button
                                onClick={handleLogout}
                                className="w-full text-xs text-[#94A3B8] hover:text-[#64748B] py-1 transition-colors"
                            >
                                Se déconnecter
                            </button>
                        </div>

                        {/* RGPD — toujours accessibles */}
                        <div className="border-t border-[#F1F5F9] mt-3 pt-3 flex items-center justify-center gap-4">
                            <button
                                onClick={handleExport}
                                disabled={exporting}
                                className="flex items-center gap-1.5 text-xs text-[#94A3B8] hover:text-[#64748B] transition-colors disabled:opacity-50"
                            >
                                {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                                Télécharger mes données
                            </button>
                            <span className="text-[#E2E8F0]">|</span>
                            <button
                                onClick={() => setStep('delete_confirm')}
                                className="flex items-center gap-1.5 text-xs text-[#94A3B8] hover:text-[#EF4444] transition-colors"
                            >
                                <Trash2 className="h-3.5 w-3.5" />
                                Supprimer mon compte
                            </button>
                        </div>
                        {exportError && (
                            <p className="mt-2 text-center text-xs text-[#EF4444]">{exportError}</p>
                        )}
                    </div>
                )}

                {/* ── Step 2 : Formulaire de churn ── */}
                {step === 'survey' && (
                    <div className="p-8">
                        <button
                            onClick={() => setStep('blocked')}
                            className="text-xs text-[#94A3B8] hover:text-[#64748B] mb-4 block"
                        >
                            ← Retour
                        </button>
                        <h2 className="text-lg font-bold text-[#1E293B] mb-1">Avant de partir…</h2>
                        <p className="text-sm text-[#64748B] mb-5">
                            Aidez-nous à améliorer BTPal en nous disant pourquoi c'est difficile pour vous.
                        </p>

                        <div className="space-y-2 mb-4">
                            {CHURN_REASONS.map(r => (
                                <label
                                    key={r.value}
                                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                                        selectedReason === r.value
                                            ? 'border-[#0F2744] bg-[#EFF6FF]'
                                            : 'border-[#E2E8F0] hover:border-[#CBD5E1]'
                                    }`}
                                >
                                    <input
                                        type="radio"
                                        name="reason"
                                        value={r.value}
                                        checked={selectedReason === r.value}
                                        onChange={() => setSelectedReason(r.value)}
                                        className="accent-[#0F2744]"
                                    />
                                    <span className="text-sm text-[#374151]">{r.label}</span>
                                </label>
                            ))}
                        </div>

                        <textarea
                            placeholder="Un commentaire supplémentaire ? (optionnel)"
                            value={comment}
                            onChange={e => setComment(e.target.value)}
                            rows={3}
                            maxLength={1000}
                            className="w-full border border-[#E2E8F0] rounded-lg p-3 text-sm text-[#1E293B] placeholder:text-[#94A3B8] resize-none focus:outline-none focus:border-[#0F2744] mb-4"
                        />

                        <button
                            onClick={handleSubmitSurvey}
                            disabled={!selectedReason || submitting}
                            className="w-full bg-[#0F2744] text-white font-semibold py-3 rounded-lg hover:bg-[#1A3A5C] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            {submitting ? (
                                <><Loader2 className="h-4 w-4 animate-spin" /> Envoi…</>
                            ) : (
                                <>Envoyer mon avis <ChevronRight className="h-4 w-4" /></>
                            )}
                        </button>
                    </div>
                )}

                {/* ── Step 3b : Confirmation suppression ── */}
                {step === 'delete_confirm' && (
                    <div className="p-8 text-center">
                        <div className="w-16 h-16 rounded-full bg-[#FEF2F2] flex items-center justify-center mx-auto mb-4">
                            <Trash2 className="h-8 w-8 text-[#DC2626]" />
                        </div>
                        <h2 className="text-xl font-bold text-[#1E293B] mb-2">Supprimer mon compte</h2>
                        <p className="text-sm text-[#64748B] mb-6 leading-relaxed">
                            Cette action est <strong>irréversible</strong>. Toutes vos données (factures, devis, chantiers, dépenses) seront définitivement supprimées.
                        </p>
                        <p className="text-xs text-[#94A3B8] mb-6">
                            Pensez à télécharger vos données avant de continuer.
                        </p>
                        <button
                            onClick={handleDeleteAccount}
                            disabled={deleting}
                            className="w-full bg-[#EF4444] text-white font-semibold py-3 rounded-lg hover:bg-[#DC2626] transition-colors disabled:opacity-50 flex items-center justify-center gap-2 mb-3"
                        >
                            {deleting ? (
                                <><Loader2 className="h-4 w-4 animate-spin" /> Suppression…</>
                            ) : (
                                <>Supprimer définitivement mon compte</>
                            )}
                        </button>
                        <button
                            onClick={() => setStep('blocked')}
                            className="w-full text-sm text-[#64748B] hover:text-[#1E293B] py-2 transition-colors"
                        >
                            ← Annuler
                        </button>
                    </div>
                )}

                {/* ── Step 3c : Merci avis churn ── */}
                {step === 'thankyou' && (
                    <div className="p-8 text-center">
                        <div className="w-16 h-16 rounded-full bg-[#F0FDF4] flex items-center justify-center mx-auto mb-4">
                            <CheckCircle className="h-8 w-8 text-[#16A34A]" />
                        </div>
                        <h2 className="text-xl font-bold text-[#1E293B] mb-2">Merci pour votre retour</h2>
                        <p className="text-sm text-[#64748B] mb-6 leading-relaxed">
                            Votre avis nous aide à améliorer BTPal. Si vous changez d'avis, vos données sont conservées.
                        </p>
                        <button
                            onClick={handleLogout}
                            className="w-full bg-[#F1F5F9] text-[#475569] font-medium py-3 rounded-lg hover:bg-[#E2E8F0] transition-colors"
                        >
                            Fermer et se déconnecter
                        </button>
                    </div>
                )}
            </div>
            </div>
        </div>,
        document.body
    );
}
